'use client';

import { useMemo } from 'react';
import { useLocale } from 'next-intl';

import {
  mergeNativeBrandFolders,
  nativeBrandHref,
} from '@/components/branding/home/branding-home-state';
import { useConvexAuth } from 'convex/react';
import { api, useQueryWithStatus } from '@/lib/convex';
import { memberOnlyDownloadArgs } from '@/lib/download-manager-backend';
import { workspaceFilesHref } from '@/lib/workspace-files-route';
import { taskAwareFolderLabel } from '@/lib/task-folder-display-name';
import { useEntityDirectoryPages } from './use-entity-directory-pages';
import {
  connectHitsToLinks,
  connectMessageSearchArgs,
} from '../connect-message-links';
import type { LinkResult, LinkScope } from '../types';

const LINK_LIMIT = 20;

function matches(term: string, ...values: Array<string | undefined>) {
  if (!term) return true;
  const lower = term.toLocaleLowerCase();
  return values.some((value) => value?.toLocaleLowerCase().includes(lower));
}

/**
 * Results for the active link scope. Each source is a bounded list the
 * workspace already loads elsewhere, filtered by the typed term here.
 * Only the active scope's query runs. A scope whose query fails shows no
 * results instead of throwing, so one refused source never crashes the page.
 */
export function useLinkScopeSearch({
  scope,
  orgSlug,
  term,
}: {
  scope: LinkScope | null;
  orgSlug?: string;
  term: string;
}): { results: LinkResult[]; isPending: boolean } {
  const locale = useLocale();
  const active = (target: LinkScope) => scope === target && Boolean(orgSlug);
  const { isLoading: authLoading } = useConvexAuth();
  const slug = orgSlug ?? '';
  const needsOrgId =
    active('connectors') || active('downloads') || active('brands');
  const organization = useQueryWithStatus(
    api.organizations.queries.getBySlug,
    needsOrgId ? { slug } : 'skip'
  );
  const organizationId = organization.data?._id;

  const connectors = useQueryWithStatus(
    api.composio_extensions.queries.listComposioConnectorCards,
    active('connectors') && organizationId ? { organizationId } : 'skip'
  );
  // Drive file and folder names: a server search, so it waits for 2+ chars.
  const files = useQueryWithStatus(
    api.vfs.public.searchByName,
    active('files') && term.trim().length >= 2
      ? { orgSlug: slug, searchTerm: term, limit: LINK_LIMIT }
      : 'skip'
  );
  // Brand folders live in Homes and Drives; `/<org>/brands` is a retired
  // namespace the VFS refuses. Same sources as the Brands page.
  // A typed term walks later pages so a brand past the first page matches.
  const brandFolders = useEntityDirectoryPages(
    api.vfs.public.listDirectoriesByEntityPaginated,
    active('brands') ? { orgSlug: slug, entity: 'branding' } : 'skip',
    term.trim().length > 0
  );
  const brandReferences = useQueryWithStatus(
    api.branding.discovery.listFolderReferences,
    active('brands') && organizationId ? { organizationId } : 'skip'
  );
  const drives = useQueryWithStatus(
    api.drives.queries.listAccessibleDrives,
    active('drives') ? { orgSlug: slug } : 'skip'
  );
  const shared = useQueryWithStatus(
    api.vfs.sharing_public.sharedWithMe,
    active('shared') ? { orgSlug: slug } : 'skip'
  );
  const channels = useQueryWithStatus(
    api.organizations.channels.queries.listChannels,
    active('channels') ? { orgSlug: slug } : 'skip'
  );
  // Connect message search (messages + thread replies, in:/from: filters).
  const messageArgs = active('channels')
    ? connectMessageSearchArgs(orgSlug, term)
    : null;
  const messages = useQueryWithStatus(
    api.organizations.channels.workspace_search.searchConnectMessages,
    messageArgs ?? 'skip'
  );
  // The menu renders outside the workspace boundary, so membership is
  // confirmed by `getBySlug` (null for a non-member); auth revalidation also
  // pauses it, like the boundary does (#6012 triage).
  const downloads = useQueryWithStatus(
    api.download_jobs.queries.getDownloadManagerFeed,
    active('downloads')
      ? memberOnlyDownloadArgs({
          organizationId,
          membershipConfirmed: organization.data != null,
          authLoading,
        })
      : 'skip'
  );

  const results = useMemo((): LinkResult[] => {
    if (!scope || !orgSlug) return [];
    const base = `/${orgSlug}`;
    let results: LinkResult[] = [];
    if (scope === 'files') {
      results = (files.data ?? []).map((entry) => ({
        id: entry.path,
        title:
          entry.kind === 'folder'
            ? taskAwareFolderLabel(entry, locale)
            : (entry.displayName ?? entry.name),
        href: workspaceFilesHref(orgSlug, entry.path),
        at: entry.updatedAt,
      }));
    } else if (scope === 'connectors') {
      results = [...(connectors.data ?? [])]
        .filter((card) => card.available || card.connected)
        .filter((card) => matches(term, card.label, card.toolkitSlug))
        .sort((left, right) => Number(right.connected) - Number(left.connected))
        .map((card) => ({
          id: card.toolkitSlug,
          title: card.label,
          href: `${base}/connectors/${encodeURIComponent(card.toolkitSlug)}`,
          hint: card.connected
            ? (card.providerAccountEmail ?? card.providerAccountLabel)
            : undefined,
        }));
    } else if (scope === 'brands') {
      results = mergeNativeBrandFolders(
        brandFolders.rows,
        brandReferences.data ?? []
      )
        .filter((entry) => matches(term, entry.displayName, entry.name))
        .map((entry) => ({
          id: entry.path,
          title: entry.displayName ?? entry.name,
          href: nativeBrandHref(orgSlug, entry),
          at: entry.modifiedAt,
        }));
    } else if (scope === 'drives') {
      results = (drives.data ?? [])
        .filter((drive) => matches(term, drive.name, drive.slug))
        .map((drive) => ({
          id: drive.slug,
          title: drive.name,
          href: workspaceFilesHref(orgSlug, drive.rootPath),
        }));
    } else if (scope === 'shared') {
      results = (shared.data?.entries ?? [])
        .filter((entry) =>
          matches(term, entry.displayName, entry.name, entry.path)
        )
        .map((entry) => ({
          id: String(entry.nodeId),
          title:
            entry.kind === 'directory'
              ? taskAwareFolderLabel(entry, locale)
              : entry.name,
          href: workspaceFilesHref(orgSlug, entry.path),
          at: entry.sharedAt,
        }));
    } else if (scope === 'channels') {
      const channelRows = (channels.data?.joined ?? [])
        .filter((entry) => matches(term, entry.channel.name))
        .map((entry) => ({
          id: String(entry.channel._id),
          title: entry.channel.name,
          href: `${base}/chats/${encodeURIComponent(String(entry.channel._id))}`,
          hint: entry.unreadCount > 0 ? String(entry.unreadCount) : undefined,
          at: entry.lastMessageAt ?? undefined,
        }));
      // Matching chats first, then matching messages (jump to message/reply).
      const messageRows = connectHitsToLinks(
        orgSlug,
        messages.data?.results ?? [],
        LINK_LIMIT,
        locale
      );
      results = [...channelRows, ...messageRows];
    } else if (scope === 'downloads') {
      results = (downloads.data ?? [])
        .filter((job) => matches(term, job.archiveName))
        .map((job) => ({
          id: String(job.jobId),
          title: job.archiveName,
          href: `${base}/downloads`,
          hint:
            job.progressPercent < 100
              ? `${Math.round(job.progressPercent)}%`
              : undefined,
          at: job.deliveredAt ?? job.startedAt,
        }));
    }
    return results.slice(0, LINK_LIMIT);
  }, [
    brandFolders.rows,
    brandReferences.data,
    channels.data,
    connectors.data,
    downloads.data,
    drives.data,
    files.data,
    locale,
    messages.data,
    orgSlug,
    scope,
    shared.data,
    term,
  ]);
  // `isPending` turns false on error, so a refused scope stops loading.
  const pendingByScope: Record<LinkScope, boolean> = {
    files: term.trim().length >= 2 && files.isPending,
    connectors: connectors.isPending,
    brands: brandFolders.isPending,
    drives: drives.isPending,
    shared: shared.isPending,
    channels: channels.isPending,
    downloads: downloads.isPending,
  };
  const isPending = Boolean(scope && orgSlug && pendingByScope[scope]);
  return { results, isPending };
}
